import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ImageHeight } from '@getaccept/lib-shared/src/enums/image-height';
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import moment from 'moment';
import type { ActionTree } from 'vuex';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { LoadImagePayload } from '@getaccept/editor-lib';
import { getLoadImageURLPayloads } from '@getaccept/editor-lib';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import { useToast } from '@getaccept/lib-shared/src/toast';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { DocumentAttachmentService } from '../../api/document-attachments/services/document-attachment.service';
import { DocumentService } from '../../api/documents/services/document.service';
import { DocumentEvent } from '../types/enums/document-event';
import { FieldAction } from '../../fields/store/field.constants';
import { DocumentHelper } from '../../helpers/document.helper';
import type { InviteRecipientInfo } from '../../signing/types/invite-recipient-info';
import type { RootState } from '../../store/root.types';
import type { DocumentState } from './document.types';
import { DocumentAction, DocumentMutation } from './document.constants';

export const actions: ActionTree<DocumentState, RootState> = {
  async [DocumentAction.LoadDocument]({ commit, dispatch }) {
    commit(DocumentMutation.SetLoading, true);
    try {
      const document: SigningPageDocument = await DocumentService.getDocument();
      dispatch(DocumentAction.LoadDocumentSuccess, document);
      dispatch(DocumentAction.LoadEditorImageURLs, document);
    } catch (error) {
      dispatch(DocumentAction.LoadDocumentFailed);
    }
  },
  async [DocumentAction.LoadDocumentSuccess]({ commit, dispatch }, document: SigningPageDocument) {
    commit(DocumentMutation.SetDocument, document);
    dispatch(DocumentAction.ExpiredReload, document);
    commit(DocumentMutation.SetLoading, false);
  },
  async [DocumentAction.LoadDocumentFailed]({ commit }) {
    commit(DocumentMutation.SetLoading, false);
    commit(DocumentMutation.SetError, true);
    useToast().toast.danger(t('failed-to-load-document'));
  },
  async [DocumentAction.LoadDocumentRevisions]({ commit, dispatch }) {
    commit(DocumentMutation.SetRevisionsLoading, true);
    try {
      const { documentRevisions } = await DocumentService.getDocumentRevisions();
      dispatch(DocumentAction.LoadDocumentRevisionsSuccess, documentRevisions);
    } catch (error) {
      dispatch(DocumentAction.LoadDocumentRevisionsFailed);
    }
  },

  [DocumentAction.LoadEditorImageURLs]({ dispatch }, document: SigningPageDocument) {
    const loadImagePayloads = document.pages
      .filter(({ editorBlock }) => editorBlock)
      .flatMap(({ editorBlock }) => getLoadImageURLPayloads(editorBlock));

    loadImagePayloads.forEach(loadImagePayload =>
      dispatch(DocumentAction.LoadEditorImageURL, loadImagePayload)
    );
  },
  async [DocumentAction.LoadEditorImageURL]({ commit }, loadImagePayload: LoadImagePayload) {
    try {
      const { url: srcUrl, externalId } = await DocumentService.getEditorImageURL(
        loadImagePayload.imageId,
        ImageHeight.QHD
      );
      commit(DocumentMutation.SetImageURL, { ...loadImagePayload, srcUrl, externalId });
    } catch (err) {
      console.error(err);
    }
  },
  [DocumentAction.LoadDocumentRevisionsSuccess](
    { commit },
    documentRevisions: DocumentRecipientRevision[]
  ) {
    commit(DocumentMutation.SetDocumentRevisions, documentRevisions);
    commit(DocumentMutation.SetRevisionsLoading, false);
  },
  [DocumentAction.LoadDocumentRevisionsFailed]({ commit }) {
    commit(DocumentMutation.SetRevisionsLoading, false);
    commit(DocumentMutation.SetError, true);
    useToast().toast.danger(t('failed-to-load-document-revision'));
  },

  async [DocumentAction.SetRevisionViewedByRecipient]({ commit, dispatch }, revision: number) {
    commit(DocumentMutation.SetRevisionsViewedLoading, true);
    try {
      await DocumentService.addRecipientView(revision);
      dispatch(DocumentAction.SetRevisionViewedByRecipientSuccess);
    } catch (error) {
      console.log(error);
    } finally {
      commit(DocumentMutation.SetRevisionsViewedLoading, false);
    }
  },
  [DocumentAction.SetRevisionViewedByRecipientSuccess]({ commit }) {
    commit(DocumentMutation.SetRevisionsViewedLoading, false);
  },
  async [DocumentAction.LoadEditorRecipientInput]({ commit, dispatch }) {
    commit(DocumentMutation.SetLoading, true);
    try {
      const editorRecipientInput: EditorRecipientInput[] = await DocumentService.loadEditorRecipientInput();
      dispatch(DocumentAction.LoadEditorRecipientInputSuccess, editorRecipientInput);
    } catch (err) {
      dispatch(DocumentAction.LoadEditorRecipientInputFailed);
    }
  },

  [DocumentAction.LoadEditorRecipientInputSuccess](
    { commit },
    editorRecipientInput: EditorRecipientInput[]
  ) {
    commit(DocumentMutation.SetEditorRecipientInput, editorRecipientInput);
    commit(DocumentMutation.SetLoading, false);
  },

  [DocumentAction.LoadEditorRecipientInputFailed]({ commit }) {
    commit(DocumentMutation.SetLoading, false);
    useToast().toast.danger(t('failed-to-load-editor-recipient-input'));
  },
  [DocumentAction.SetEditorRecipientInput](
    { commit },
    editorRecipientInput: EditorRecipientInput[]
  ) {
    commit(DocumentMutation.SetEditorRecipientInput, editorRecipientInput);
  },

  async [DocumentAction.SetFocusedRequiredId]({ commit }, focusedRequiredId: string) {
    commit(DocumentMutation.SetFocusedRequiredId, focusedRequiredId);
  },
  [DocumentAction.CompleteSigning](
    { commit },
    { signeesWaiting, recipientId }: { signeesWaiting: number; recipientId: string }
  ) {
    const status: RecipientStatus = RecipientStatus.Signed;
    commit(DocumentMutation.SetRecipientStatus, { recipientId, status });
    if (signeesWaiting === 0) {
      useToast().toast.info(t('loading-document-certificate'));
      commit(DocumentMutation.SetDocumentSignedDate, moment().toISOString());
    }
  },
  async [DocumentAction.SetAttachmentView]({ commit }, id: string) {
    commit(DocumentMutation.SetAttachmentView, id);
  },
  async [DocumentAction.UpdateAttachments]({ commit }, document: SigningPageDocument) {
    const oldAttachments: DocumentAttachment[] = (document && document.documentAttachments) || [];
    try {
      const documentAttachments: DocumentAttachment[] = await DocumentAttachmentService.load();
      commit(DocumentMutation.SetDocumentAttachments, documentAttachments);
    } catch (error) {
      commit(DocumentMutation.SetDocumentAttachments, oldAttachments);
    }
  },
  async [DocumentAction.RejectDocument](
    _,
    {
      reason,
      recipientId,
      documentId,
      authId,
    }: { reason: string; recipientId: string; documentId: string; authId: string }
  ) {
    await DocumentService.reject(reason, documentId, recipientId, authId);
  },

  async [DocumentAction.LoadAfterSign]({ dispatch }) {
    dispatch(DocumentAction.LoadDocument);
    dispatch(`fields/${FieldAction.Load}`);
  },
  async [DocumentAction.SendDocumentStatus](
    { dispatch, state, rootState, commit },
    { newDocumentStatus }: { newDocumentStatus: DocumentStatus }
  ) {
    if (rootState.isPreview) {
      return;
    }
    const {
      document: { recipients, status },
    }: DocumentState = state;
    const recipient = DocumentHelper.getRecipientById(rootState.recipientId, recipients);

    if (status === newDocumentStatus && status === recipient.status) {
      return;
    }
    switch (newDocumentStatus) {
      case DocumentStatus.Reviewed:
        await dispatch(DocumentAction.SendEventLog, { event: DocumentEvent.Reviewed });
        commit(DocumentMutation.SetDocumentStatus, DocumentStatus.Reviewed);
        break;
      case DocumentStatus.Viewed:
        commit(DocumentMutation.SetDocumentStatus, DocumentStatus.Viewed);
        break;
      case DocumentStatus.Rejected:
        await dispatch(DocumentAction.SendEventLog, { event: DocumentEvent.Rejected });
        commit(DocumentMutation.SetDocumentStatus, DocumentStatus.Rejected);
        break;
    }
  },
  async [DocumentAction.SetRecipientStatus](
    { dispatch, rootState, state, commit },
    { status }: { status: RecipientStatus }
  ) {
    const { recipientId }: RootState = rootState;
    const {
      document: { recipients },
    }: DocumentState = state;
    const recipient = DocumentHelper.getRecipientById(recipientId, recipients);
    switch (status) {
      case RecipientStatus.Reviewed:
        commit(DocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: RecipientStatus.Reviewed,
        });
        await dispatch(DocumentAction.SendEventLog, { event: DocumentEvent.Reviewed });
        break;
      case RecipientStatus.Viewed:
        commit(DocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: RecipientStatus.Viewed,
        });
        break;
      case RecipientStatus.Rejected:
        commit(DocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: RecipientStatus.Rejected,
        });
        await dispatch(DocumentAction.SendEventLog, { event: DocumentEvent.Rejected });
        break;
    }
  },
  async [DocumentAction.SendEventLog]({ rootState, state }, { event }: { event: DocumentEvent }) {
    try {
      const { recipientId, authId }: RootState = rootState;
      const {
        document: { id: documentId },
      }: DocumentState = state;
      await DocumentService.logEvent(documentId, recipientId, authId, event);
    } catch (error) {
      console.error(error);
    }
  },
  async [DocumentAction.LoadDocumentAttachments]({ commit }) {
    try {
      const documentAttachments: DocumentAttachment[] = await DocumentAttachmentService.load();
      commit(DocumentMutation.SetDocumentAttachments, documentAttachments);
    } catch (e) {
      useToast().toast.danger(t('failed-to-load-attachments'));
    }
  },
  [DocumentAction.ApproveDocument]({ commit }, { recipientId }: { recipientId: string }) {
    const status: RecipientStatus = RecipientStatus.Approved;
    commit(DocumentMutation.SetRecipientStatus, { recipientId, status });
  },
  [DocumentAction.ExpiredReload](_, { expirationDate }: SigningPageDocument) {
    const timeout: number = moment(expirationDate).diff(moment());
    const maxTimeout: number = 8 * 3600 * 1000;
    if (timeout > 0) {
      setTimeout(() => {
        window.location.reload();
      }, Math.min(timeout, maxTimeout));
    }
  },
  async [DocumentAction.ForwardToRecipient](
    { rootState, state, dispatch, commit },
    recipientInfo: InviteRecipientInfo
  ) {
    try {
      commit(DocumentMutation.SetButtonLoading, true);
      const { recipientId, authId }: RootState = rootState;
      const {
        document: { id: documentId },
      }: DocumentState = state;
      const response: { status: number; error: string } = await DocumentService.forward(
        documentId,
        recipientId,
        authId,
        recipientInfo
      );

      if (response.status === 1) {
        dispatch(DocumentAction.ForwardToRecipientSuccess, recipientInfo);
      } else {
        dispatch(DocumentAction.ForwardToRecipientFailed);
      }
    } catch (e) {
      dispatch(DocumentAction.ForwardToRecipientFailed);
    }
  },
  async [DocumentAction.ForwardToRecipientSuccess](
    { dispatch, commit },
    recipientInfo: InviteRecipientInfo
  ) {
    dispatch(DocumentAction.SendEventLog, { event: DocumentEvent.Forwarded });
    if (recipientInfo.transferSignature) {
      dispatch(`fields/${FieldAction.Load}`, { root: true });
    }
    dispatch(DocumentAction.SetForwardStatus, true);

    dispatch(DocumentAction.UpdateRecipients);
    commit(DocumentMutation.SetButtonLoading, false);
  },
  [DocumentAction.ForwardToRecipientFailed]({ commit }) {
    useToast().toast.danger(t('failed-to-invite-recipient'));
    commit(DocumentMutation.SetButtonLoading, false);
  },
  async [DocumentAction.UpdateRecipients]({ commit }) {
    const recipients: Recipient[] = await DocumentService.getRecipients();
    commit(DocumentMutation.UpdateRecipients, recipients);
  },
  [DocumentAction.SetForwardStatus]({ commit }, forwarded: boolean) {
    commit(DocumentMutation.SetForwarded, forwarded);
  },
  async [DocumentAction.GetPdfUrl]({ dispatch }) {
    try {
      const pdfUrl: string = await DocumentService.getDownloadUrl();
      dispatch(DocumentAction.GetPdfUrlSuccess, pdfUrl);
    } catch (e) {
      dispatch(DocumentAction.GetPdfUrlFailed);
    }
  },
  [DocumentAction.GetPdfUrlSuccess]({ commit }, pdfUrl: string) {
    commit(DocumentMutation.SetPdfUrl, pdfUrl);
  },
  [DocumentAction.GetPdfUrlFailed]() {
    useToast().toast.danger(t('failed-to-download-pdf'));
  },
  [DocumentAction.SetDocumentStatus]({ commit }, status: DocumentStatus) {
    commit(DocumentMutation.SetDocumentStatus, status);
  },
  async [DocumentAction.DownloadDocument]({ commit, dispatch }) {
    commit(DocumentMutation.SetIsDownloading, true);
    try {
      await DocumentService.download();
    } catch {
      dispatch(DocumentAction.DownloadDocumentFailed);
    }
  },
  [DocumentAction.DownloadDocumentComplete]({ commit }) {
    commit(DocumentMutation.SetIsDownloading, false);
  },
  [DocumentAction.DownloadDocumentFailed]({ commit }) {
    commit(DocumentMutation.SetIsDownloading, false);
    useToast().toast.danger(t('failed-to-download-document'));
  },
  async [DocumentAction.LoadPartiallySignedFields]({ commit }) {
    try {
      const partiallySignedFields = await DocumentService.loadPartiallySignedFields();
      commit(DocumentMutation.SetPartiallySignedFields, partiallySignedFields);
    } catch (error) {
      useToast().toast.danger(t('could_not_load_data_please_try_again_or_contact_support'));
    }
  },
};
